import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { UserService } from '../../../pages/administaration/ProfileInfo/Services/UserProfileService';
import { Page } from '../../../pages/administaration/PageRoles/Model/pageRoles';
import { tap, catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class UserPermissionService {
  
  private userPagesSubject = new BehaviorSubject<Page[]>([]);
  private isLoadedSubject = new BehaviorSubject<boolean>(false);
  private isLoadingSubject = new BehaviorSubject<boolean>(false);

  
  public userPages$ = this.userPagesSubject.asObservable();
  public isLoaded$ = this.isLoadedSubject.asObservable();
  public isLoading$ = this.isLoadingSubject.asObservable();

  constructor(private userService: UserService) {
    this.initializeUserPages();
  }

  private initializeUserPages(): void {
    const token = sessionStorage.getItem('serviceAdmintoken');
    if (token && !this.isLoadedSubject.value && !this.isLoadingSubject.value) {
      this.loadUserPages().subscribe();
    }
  }


  loadUserPages(): Observable<Page[]> {
   
    if (this.isLoadingSubject.value) {
      return this.userPages$;
    }

    
    if (this.isLoadedSubject.value) {
      return of(this.userPagesSubject.value);
    }

   
    this.isLoadingSubject.next(true);

    return this.userService.getUserPages().pipe(
      tap((pages: Page[]) => {
 
        this.userPagesSubject.next(pages);
        this.isLoadedSubject.next(true);
        this.isLoadingSubject.next(false);
        console.log('User pages loaded and cached:', pages);
      }),
      catchError((error) => {
        console.error('Error loading user pages:', error);
        this.isLoadingSubject.next(false);
        this.isLoadedSubject.next(false);
        return of([]);
      })
    );
  }


  hasAccessToPage(path: string): boolean {
    const normalizedPath = this.normalizePath(path);
    
   
    if (normalizedPath === 'admin' || normalizedPath === '') {
      return true;
    }
    
    const userPages = this.userPagesSubject.value;
    
    if (!userPages || userPages.length === 0) {
      return false;
    }

    return this.checkPageInTree(userPages, normalizedPath);
  }

  getUserPages(): Page[] {
    return this.userPagesSubject.value;
  }


  arePagesLoaded(): boolean {
    return this.isLoadedSubject.value;
  }

  isCurrentlyLoading(): boolean {
    return this.isLoadingSubject.value;
  }

 
  clearUserPages(): void {
    this.userPagesSubject.next([]);
    this.isLoadedSubject.next(false);
    this.isLoadingSubject.next(false);
  }


  refreshUserPages(): Observable<Page[]> {
    this.clearUserPages();
    return this.loadUserPages();
  }

 
  getPageByPath(path: string): Page | null {
    const userPages = this.userPagesSubject.value;
    if (!userPages || userPages.length === 0) {
      return null;
    }

    return this.findPageInTree(userPages, this.normalizePath(path));
  }

  
  private checkPageInTree(pages: Page[], targetPath: string): boolean {
    for (const page of pages) {
      const pagePath = this.normalizePath(page.link);
      
      if (pagePath === targetPath) {
        return true;
      }

      if (page.children && page.children.length > 0) {
        if (this.checkPageInTree(page.children, targetPath)) {
          return true;
        }
      }
    }
    return false;
  }



  private findPageInTree(pages: Page[], targetPath: string): Page | null {
    for (const page of pages) {
      const pagePath = this.normalizePath(page.link);
      
      if (pagePath === targetPath) {
        return page;
      }

      
      if (page.children && page.children.length > 0) {
        const found = this.findPageInTree(page.children, targetPath);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }


  private normalizePath(path: string): string {
    return path ? path.replace(/^\/+/, '') : '';
  }
}



